import React from 'react';

/**
 * Подсветка совпадений в тексте в зависимости от метода поиска.
 *
 * - fulltext / ilike / trigram → подсветка слов запроса жёлтым
 * - exact_code / prefix_code → подсветка кода товара зелёным
 * - trademark_ilike / trademark → подсветка бренда оранжевым
 * - similar → подсветка общих слов + процент схожести
 */
export default function MatchHighlighter({ text, query, searchMethod, productCode }) {
  if (!text) return <span className="text-text-disabled italic">Нет описания</span>;
  if (!query && !searchMethod) return <span>{text}</span>;

  const methods = (searchMethod || '').split(',').map(s => s.trim().toLowerCase());

  // Определяем что подсвечивать
  const highlights = [];

  // Для кодовых методов — подсветка кода в тексте
  if (methods.some(m => m.includes('exact') || m.includes('prefix'))) {
    const code = query || productCode || '';
    if (code && /^\d+$/.test(code.trim())) {
      highlights.push({ words: [code.trim()], className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300 rounded px-0.5 font-medium' });
    }
  }

  // Для trademark-методов — подсветка бренда
  if (methods.some(m => m.includes('trademark'))) {
    if (query) {
      highlights.push({ words: [query.trim()], className: 'bg-orange-100 text-orange-800 dark:bg-orange-950/40 dark:text-orange-300 rounded px-0.5 font-medium' });
    }
  }

  // Для текстовых методов — подсветка слов запроса
  if (methods.some(m => ['fulltext', 'ilike', 'trigram', 'semantic'].some(k => m.includes(k)))) {
    if (query) {
      const words = query.split(/\s+/).filter(w => w.length > 1);
      if (words.length > 0) {
        highlights.push({ words, className: 'bg-yellow-200 text-yellow-900 dark:bg-yellow-500/30 dark:text-yellow-100 rounded px-0.5' });
      }
    }
  }

  // Для similar — подсвечиваем общие слова из query если есть
  if (methods.some(m => m.includes('similar')) && query) {
    const words = query.split(/\s+/).filter(w => w.length > 2);
    if (words.length > 0) {
      highlights.push({ words, className: 'bg-purple-100 text-purple-800 dark:bg-purple-950/40 dark:text-purple-300 rounded px-0.5' });
    }
  }

  // Если нечего подсвечивать — fallback на слова запроса
  if (highlights.length === 0 && query) {
    const words = query.split(/\s+/).filter(w => w.length > 1);
    if (words.length > 0) {
      highlights.push({ words, className: 'bg-yellow-200 text-yellow-900 dark:bg-yellow-500/30 dark:text-yellow-100 rounded px-0.5' });
    }
  }

  if (highlights.length === 0) return <span>{text}</span>;

  // Собираем все слова в один регекс
  const allWords = highlights.flatMap(h => h.words);
  const classMap = {};
  highlights.forEach(h => {
    h.words.forEach(w => { classMap[w.toLowerCase()] = h.className; });
  });

  try {
    const escaped = allWords.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const regex = new RegExp(`(${escaped.join('|')})`, 'gi');
    const parts = text.split(regex);

    return (
      <span>
        {parts.map((part, i) => {
          const lower = part.toLowerCase();
          // Находим класс для этого слова
          const cls = allWords.find(w => w.toLowerCase() === lower);
          if (cls) {
            return <mark key={i} className={classMap[lower] || 'bg-yellow-200 dark:bg-yellow-500/30 dark:text-yellow-100'}>{part}</mark>;
          }
          return <span key={i}>{part}</span>;
        })}
      </span>
    );
  } catch {
    return <span>{text}</span>;
  }
}

/**
 * Бейдж метода поиска для правой панели.
 */
export function MethodBadge({ method }) {
  if (!method) return null;

  const methods = method.split(',').map(s => s.trim());

  const colorMap = {
    exact_code: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
    prefix_code: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
    fulltext: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
    trigram: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
    semantic: 'bg-purple-100 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300',
    ilike: 'bg-app text-text-muted',
    ilike_description: 'bg-app text-text-muted',
    trademark: 'bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300',
    trademark_ilike: 'bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300',
    trademark_trigram: 'bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300',
    importer: 'bg-teal-100 text-teal-700 dark:bg-teal-950/40 dark:text-teal-300',
    similar: 'bg-purple-100 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300',
  };

  const labelMap = {
    exact_code: 'Код точно',
    prefix_code: 'Код по началу',
    fulltext: 'Слова',
    trigram: 'Похожее',
    semantic: 'Похожие',
    ilike: 'Подстрока',
    ilike_description: 'Подстрока',
    trademark: 'Марка',
    trademark_ilike: 'Марка точная',
    trademark_trigram: 'Марка похожая',
    importer: 'Импортёр',
    similar: 'Похожий',
  };

  return (
    <div className="flex flex-wrap gap-1">
      {methods.map((m, i) => (
        <span
          key={i}
          className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${colorMap[m] || 'bg-app text-text-muted'}`}
          title={`Технически: ${m}`}
        >
          {labelMap[m] || m}
        </span>
      ))}
    </div>
  );
}
